import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Menu, X, LogOut, ShoppingCart } from "lucide-react";
import { primaryHomeForRoles } from "@/lib/auth";

const NAV = [
  { to: "/catalogo", label: "Catálogo" },
  { to: "/tailor-made", label: "Tailor-Made" },
  { to: "/partners", label: "Partners" },
  { to: "/equipo", label: "Equipo" },
  { to: "/contacto", label: "Contacto" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const nav = useNavigate();
  const cartCount = cart?.items?.length || 0;

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link">
          <Logo />
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive ? "text-[#0B0B0B] bg-black/5" : "text-[#0B0B0B] hover:text-[#0B0B0B] hover:bg-black/5"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-2">
          <Link
            to="/carrito"
            data-testid="nav-cart-btn"
            aria-label="Ver carrito"
            className="relative p-2 rounded-md text-[#0B0B0B] hover:bg-black/5"
          >
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full bg-[#F26A21] text-[#0B0B0B]"
              >
                {cartCount}
              </span>
            )}
          </Link>
          {user ? (
            <>
              <button
                onClick={() => nav(primaryHomeForRoles(user.roles))}
                data-testid="nav-my-dashboard-btn"
                className="text-sm px-3 py-1.5 rounded-md border border-gray-300 text-[#0B0B0B] hover:bg-black/5"
              >
                Mi panel
              </button>
              <button
                onClick={logout}
                data-testid="nav-logout-btn"
                className="text-sm px-3 py-1.5 rounded-md text-[#0B0B0B] hover:text-[#0B0B0B]"
              >
                <LogOut className="inline w-4 h-4 mr-1" /> Salir
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-btn"
                className="text-sm px-3 py-1.5 rounded-md text-[#0B0B0B] hover:text-[#0B0B0B]"
              >
                Ingresar
              </Link>
              <Link
                to="/registro"
                data-testid="nav-register-btn"
                className="text-sm px-4 py-1.5 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] transition-colors"
              >
                Crear cuenta
              </Link>
            </>
          )}
        </div>
        <div className="md:hidden flex items-center gap-1">
          <Link
            to="/carrito"
            data-testid="nav-cart-btn-mobile"
            aria-label="Ver carrito"
            className="relative p-2 text-[#0B0B0B]"
          >
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute top-0 right-0 min-w-[16px] h-[16px] px-1 flex items-center justify-center text-[9px] font-bold rounded-full bg-[#F26A21] text-[#0B0B0B]">
                {cartCount}
              </span>
            )}
          </Link>
          <button
            className="text-[#0B0B0B] p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-3 flex flex-col gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="px-3 py-2 text-sm text-[#0B0B0B] hover:bg-black/5 rounded-md"
              >
                {n.label}
              </Link>
            ))}
            {user ? (
              <>
                <button
                  onClick={() => { setOpen(false); nav(primaryHomeForRoles(user.roles)); }}
                  className="text-left px-3 py-2 text-sm text-[#0B0B0B] bg-black/5 rounded-md"
                >
                  Mi panel
                </button>
                <button
                  onClick={() => { setOpen(false); logout(); }}
                  className="text-left px-3 py-2 text-sm text-[#0B0B0B]"
                >
                  Salir
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2 text-sm text-[#0B0B0B]">
                  Ingresar
                </Link>
                <Link to="/registro" onClick={() => setOpen(false)} className="px-3 py-2 text-sm text-[#0B0B0B] bg-[#F26A21] rounded-md">
                  Crear cuenta
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
